/**
 * GlobalFooter Component
 * 
 * Canonical footer for all customer-facing pages with variant support.
 * 
 * Variants:
 * - 'full': Complete footer with brand, legal, support, contact, social (for informational pages)
 * - 'compact': Minimal legal links + copyright (for transactional pages)
 * - 'mobile': Very small legal strip (for mobile, non-sticky, below content)
 * 
 * Batch 4.3A: Footer Standardization Phase 1
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { COMPANY, FOOTER_LEGAL_LINKS, FOOTER_SUPPORT_LINKS, FOOTER_TRUST_BADGES } from '../../../config/companyConfig';
import YovoTripLogo from '../YovoTripLogo';
import { listPopularFooterFlightRouteLinks } from '../../../content/flight-seo/indexedRouteLinks';
import styles from './GlobalFooter.module.css';

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

// Link columns for full footer - no fake destination routes
const LINK_COLUMNS = [
  {
    heading: 'Legal',
    links: FOOTER_LEGAL_LINKS,
  },
  {
    heading: 'Support',
    links: FOOTER_SUPPORT_LINKS,
  },
];

// Compact footer styles (inline for simplicity, content-driven height)
const COMPACT_FOOTER_SX = {
  borderTop: '1px solid #E5E7EB',
  py: 2,
  px: 2,
  mt: 'auto',
  bgcolor: '#FAFAFA',
};

const COMPACT_LINKS_SX = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1.5,
  mb: 1,
};

const COMPACT_LINK_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '0.75rem',
  lineHeight: '18px',
  color: '#6B7280',
  textDecoration: 'none',
  '&:hover': {
    color: '#374151',
    textDecoration: 'underline',
  },
};

const COMPACT_SEPARATOR_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  color: '#D1D5DB',
  fontSize: '0.75rem',
  lineHeight: '18px',
};

const COMPACT_COPYRIGHT_SX = {
  fontSize: '0.7rem',
  color: '#9CA3AF',
  textAlign: 'center',
};

// Mobile footer styles (very slim)
const MOBILE_FOOTER_SX = {
  borderTop: '1px solid #E5E7EB',
  py: 1.5,
  px: 2,
  bgcolor: '#FAFAFA',
};

const MOBILE_LINKS_SX = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: 1,
  mb: 0.5,
};

const MOBILE_LINK_SX = {
  fontSize: '0.65rem',
  color: '#6B7280',
  textDecoration: 'none',
};

const MOBILE_COPYRIGHT_SX = {
  fontSize: '0.6rem',
  color: '#9CA3AF',
  textAlign: 'center',
};

/**
 * GlobalFooter with variant support
 * @param {string} variant - 'full' | 'compact' | 'mobile'
 */
export default function GlobalFooter({ variant = 'full' }) {
  const socialLinks = [
    { icon: FacebookIcon, href: COMPANY.social?.facebook, label: 'YovoTrip on Facebook' },
    { icon: XIcon, href: COMPANY.social?.twitter, label: 'YovoTrip on X' },
    { icon: InstagramIcon, href: COMPANY.social?.instagram, label: 'YovoTrip on Instagram' },
    { icon: LinkedInIcon, href: COMPANY.social?.linkedin, label: 'YovoTrip on LinkedIn' },
  ].filter(link => link.href);

  const currentYear = new Date().getFullYear();
  const popularRoutes = listPopularFooterFlightRouteLinks();

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPACT VARIANT - Minimal legal footer for transactional pages
  // ═══════════════════════════════════════════════════════════════════════════
  if (variant === 'compact') {
    return (
      <Box component="footer" sx={COMPACT_FOOTER_SX}>
        <Box sx={COMPACT_LINKS_SX}>
          {FOOTER_LEGAL_LINKS.map((link, index) => (
            <React.Fragment key={link.path}>
              <Box component={Link} to={link.path} sx={COMPACT_LINK_SX}>
                {link.label}
              </Box>
              {index < FOOTER_LEGAL_LINKS.length - 1 && (
                <Typography component="span" sx={COMPACT_SEPARATOR_SX}>|</Typography>
              )}
            </React.Fragment>
          ))}
          <Typography component="span" sx={COMPACT_SEPARATOR_SX}>|</Typography>
          <Box component={Link} to="/contact" sx={COMPACT_LINK_SX}>
            Contact Us
          </Box>
        </Box>
        <Typography sx={COMPACT_COPYRIGHT_SX}>
          &copy; {currentYear} {COMPANY.brandName}. All rights reserved.
        </Typography>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MOBILE VARIANT - Very slim legal strip for mobile pages
  // ═══════════════════════════════════════════════════════════════════════════
  if (variant === 'mobile') {
    return (
      <Box component="footer" sx={MOBILE_FOOTER_SX}>
        <Box sx={MOBILE_LINKS_SX}>
          {FOOTER_LEGAL_LINKS.map((link, index) => (
            <React.Fragment key={link.path}>
              <Link to={link.path} style={{ textDecoration: 'none' }}>
                <Typography component="span" sx={MOBILE_LINK_SX}>
                  {link.label}
                </Typography>
              </Link>
              {index < FOOTER_LEGAL_LINKS.length - 1 && (
                <Typography component="span" sx={{ ...MOBILE_LINK_SX, color: '#D1D5DB' }}>·</Typography>
              )}
            </React.Fragment>
          ))}
        </Box>
        <Typography sx={MOBILE_COPYRIGHT_SX}>
          &copy; {currentYear} {COMPANY.brandName}
        </Typography>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // FULL VARIANT - Complete footer for informational pages
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <footer className={styles.footer}>
      <div className={styles.inner}>
        {/* Brand Section */}
        <div className={styles.brand}>
          <Link to="/" className={styles.logo} aria-label={COMPANY.brandName}>
            <YovoTripLogo color="#ffffff" title={COMPANY.brandName} />
          </Link>
          <p className={styles.tagline}>{COMPANY.tagline}</p>

          {FOOTER_TRUST_BADGES.length > 0 && (
            <ul className={styles.trustBadges}>
              {FOOTER_TRUST_BADGES.map((badge) => (
                <li key={badge.label} className={styles.trustBadge}>
                  <span className={styles.trustBadgeMark} aria-hidden="true">✓</span>
                  {badge.label}
                </li>
              ))}
            </ul>
          )}
          
          {/* Social Links */}
          {socialLinks.length > 0 && (
            <div className={styles.socials}>
              {socialLinks.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  className={styles.socialBtn}
                  aria-label={label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Link Columns */}
        <div className={styles.linkCols}>
          {LINK_COLUMNS.map((col) => (
            <div key={col.heading} className={styles.linkCol}>
              <h4 className={styles.colHeading}>{col.heading}</h4>
              <ul className={styles.linkList}>
                {col.links.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className={styles.link}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact Column */}
          <div className={styles.linkCol}>
            <h4 className={styles.colHeading}>Contact</h4>
            <ul className={styles.linkList}>
              <li>
                <a href={`mailto:${COMPANY.contact.supportEmail}`} className={styles.link}>
                  {COMPANY.contact.supportEmail}
                </a>
              </li>
              <li>
                <span className={styles.contactNote}>
                  Grievance Officer
                </span>
                <a href={`mailto:${COMPANY.contact.grievanceEmail}`} className={styles.link}>
                  {COMPANY.contact.grievanceEmail}
                </a>
              </li>
            </ul>
          </div>

          {popularRoutes.length > 0 && (
            <div className={styles.linkCol}>
              <h4 className={styles.colHeading}>Popular Routes</h4>
              <ul className={styles.linkList}>
                {popularRoutes.map((route) => (
                  <li key={route.slug}>
                    <Link to={route.href} className={styles.link}>
                      {route.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/sitemap" className={`${styles.link} ${styles.viewAllLink}`}>
                    View all routes →
                  </Link>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className={styles.bottom}>
        <p className={styles.copy}>
          &copy; {currentYear} {COMPANY.brandName}. All rights reserved.
        </p>
        <div className={styles.bottomLinks}>
          {FOOTER_LEGAL_LINKS.map((link, index) => (
            <React.Fragment key={link.path}>
              <Link to={link.path} className={styles.bottomLink}>
                {link.label}
              </Link>
              {index < FOOTER_LEGAL_LINKS.length - 1 && (
                <span className={styles.separator}>|</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </footer>
  );
}
